/**
 * Ce qu'on demande à une caméra : **plus que ce qu'on publie**, pour qu'il
 * reste des pixels à rogner.
 *
 * Ce fut « de quoi remplir la couche haute, pas plus » — 1280×720, la
 * définition de cette couche. C'était juste tant que le cadrage n'existait
 * pas ; dès qu'on rogne, ce plafond-là devient le plancher de tout ce qui
 * suit. Zoomer 2× n'y laissait que 640×360, et l'image étalée sur le rouleau
 * était molle.
 *
 * La capture vise donc 1080p, et c'est le **plan de cadrage** qui plafonne ce
 * qui part dans l'encodeur (`MAX_OUTPUT_PIXELS`). Le débit ne bouge pas : on
 * publie toujours au plus 1280×720. Ce qui change, c'est qu'à 2× on publie
 * désormais 960×540 au lieu de 640×360, et qu'à 1× on publie une réduction du
 * 1080p plutôt que le 720p brut — plus fine.
 *
 * `ideal` et non `exact` : un appareil qui ne sait pas faire de 1080p donne ce
 * qu'il a, et tout le reste s'y adapte.
 *
 * Ce que ça coûte : la capture et l'encodage d'une image plus grande sur
 * l'appareil. Jamais mesuré sur un vrai téléphone.
 */
export const CAMERA_CAPTURE: MediaTrackConstraints = {
  width: { ideal: 1920 },
  height: { ideal: 1080 },
  frameRate: { ideal: 30, max: 30 },
};

/**
 * Ouvre la caméra de ce poste et rend sa piste vidéo. Le refus du navigateur
 * remonte tel quel : c'est à l'écran de le dire en mots (`camera-failure.ts`).
 */
export async function openCameraTrack(deviceId: string | null): Promise<MediaStreamTrack> {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: deviceId === null ? CAMERA_CAPTURE : { ...CAMERA_CAPTURE, deviceId: { exact: deviceId } },
    audio: false,
  });
  const track = stream.getVideoTracks()[0];

  if (track === undefined) {
    for (const other of stream.getTracks()) {
      other.stop();
    }

    throw new DOMException("Aucune piste vidéo.", "NotFoundError");
  }

  return track;
}
